import { api } from '../api.js';
import { auth } from '../auth.js';
import { escapeHtml, showToast, formatBytes, uploadType } from '../utils.js';
import { openViewer } from '../components/viewer.js';
import { tileMenuHtml, closeTileMenus, toggleTileMenu, bindTileMenuDismissal } from '../components/tile-menu.js';

/**
 * The owner's gallery: upload, edit, share, delete.
 *
 * The biggest change from v1 is that images are no longer auth-only blobs fetched through
 * the API and turned into object URLs. The API returns short-lived presigned S3 URLs, so
 * a tile is an ordinary <img src>. All of v1's getImageBlob/object-URL bookkeeping is gone.
 */

const MAX_UPLOAD_BYTES = 32 * 1024 * 1024;
const PENDING_POLL_MS = 3000;
/**
 * Every upload is type-checked and scanned for malware before it appears, which usually takes
 * a few seconds and occasionally a minute or two. Three minutes of polling covers that without
 * leaving a page with a genuinely stuck image polling forever.
 */
const PENDING_POLL_LIMIT = 60;
/** Still going through the upload checks or the derivatives, as opposed to settled. */
const IN_PROGRESS = new Set(['pending', 'checking']);

let state = {
    images: [],
    collections: [],
    search: '',
    collection: '',
    pollTimer: null,
    /**
     * Uploads started from this page: id -> { name, collection }. A file that fails its type
     * check or contains malware is deleted along with its row, so an upload vanishing from the
     * list is how a failure shows up -- and this is what lets the page say so.
     */
    uploads: new Map(),
};

/**
 * The lists and filters belong to whoever was signed in. Without this, a collection filter chosen
 * by one account was applied to the next account's photos after signing out and in in the same tab.
 */
function forget() {
    stopPolling();
    state.images = [];
    state.collections = [];
    state.search = '';
    state.collection = '';
    state.uploads.clear();
}

window.addEventListener('auth:logout', forget);
window.addEventListener('auth:login', forget);

const app = () => document.getElementById('app');

export async function renderGallery() {
    stopPolling();
    state.uploads.clear();

    app().innerHTML = `
        <div class="container gallery-container">
            <div class="gallery-header">
                <h1 class="gallery-title">Gallery</h1>
                <div class="gallery-filters">
                    <div class="input-wrapper">
                        <input type="text" id="search-input" placeholder="Search captions...">
                    </div>
                    <select id="collection-filter" class="form-select">
                        <option value="">All collections</option>
                    </select>
                    <button type="button" id="upload-btn" class="btn btn-primary">
                        <i class="fa-solid fa-upload"></i> Upload
                    </button>
                </div>
            </div>
            <input type="file" id="file-input" accept="image/*,.heic,.heif" multiple hidden>
            <div id="upload-status"></div>
            <div id="gallery-body"><p class="text-muted">Loading your photos...</p></div>
        </div>`;

    bindControls();
    await loadImages();
}

function bindControls() {
    const fileInput = document.getElementById('file-input');

    // The header controls and the mobile drawer controls are two sets of elements
    // driving the same behaviour, so bind both wherever they exist.
    const openPicker = () => fileInput.click();
    document.getElementById('upload-btn')?.addEventListener('click', openPicker);
    document.getElementById('mobile-upload-btn')?.addEventListener('click', openPicker);

    fileInput.addEventListener('change', async () => {
        const files = [...fileInput.files];
        fileInput.value = '';
        if (files.length) await uploadFiles(files);
    });

    const onSearch = (value) => {
        state.search = value.trim().toLowerCase();
        renderTiles();
    };
    document.getElementById('search-input')?.addEventListener('input', (e) => onSearch(e.target.value));
    document.getElementById('mobile-search-input')?.addEventListener('input', (e) => onSearch(e.target.value));

    const onCollection = (value) => {
        state.collection = value;
        loadImages();
    };
    document.getElementById('collection-filter')?.addEventListener('change', (e) => onCollection(e.target.value));
    document.getElementById('mobile-collection-filter')?.addEventListener('change', (e) => onCollection(e.target.value));
}

async function loadImages() {
    try {
        const [imagesRes, collectionsRes] = await Promise.all([
            api.listImages(state.collection ? { collection: state.collection } : {}),
            api.listCollections(),
        ]);
        state.images = imagesRes.images ?? [];
        // The API returns {collection, count} rows, not bare strings. Normalize once here
        // so every consumer below deals with one shape.
        state.collections = (collectionsRes.collections ?? []).map((c) => ({
            name: typeof c === 'string' ? c : c.collection,
            count: typeof c === 'string' ? null : Number(c.count),
        })).filter((c) => c.name);
    } catch (err) {
        document.getElementById('gallery-body').innerHTML =
            `<div class="error-state"><p>${escapeHtml(err.message ?? 'Could not load your photos')}</p></div>`;
        return;
    }

    reportFinishedUploads();
    renderCollectionOptions();
    renderTiles();
    schedulePendingPoll();
}

function renderCollectionOptions() {
    for (const id of ['collection-filter', 'mobile-collection-filter']) {
        const select = document.getElementById(id);
        if (!select) continue;
        select.innerHTML = `<option value="">All collections</option>` +
            state.collections
                .map((c) => {
                    const label = c.count === null ? c.name : `${c.name} (${c.count})`;
                    return `<option value="${escapeHtml(c.name)}">${escapeHtml(label)}</option>`;
                })
                .join('');
        select.value = state.collection;
    }
}

function visibleImages() {
    if (!state.search) return state.images;
    return state.images.filter((img) => (img.caption ?? '').toLowerCase().includes(state.search));
}

function statusLabel(status) {
    if (IN_PROGRESS.has(status)) return 'Checking upload…';
    if (status === 'unchecked') return "Couldn't be checked";
    return 'Processing failed';
}

function tileHtml(img) {
    const caption = img.caption?.trim();
    const ready = img.status === 'ready';
    const checking = IN_PROGRESS.has(img.status);

    // Only a ready image has a thumbnail, and only a ready image can be viewed or downloaded:
    // anything else has not been cleared by the upload checks yet, or never will be.
    const media = ready
        ? `<img class="gallery-thumb" src="${escapeHtml(img.thumbnail_url ?? '')}" alt="${escapeHtml(caption ?? 'Photo')}" loading="lazy">`
        : `<div class="gallery-thumb gallery-thumb-state ${checking ? 'is-checking' : 'is-problem'}">
               <i class="fa-solid ${checking ? 'fa-shield-halved' : 'fa-triangle-exclamation'}"></i>
               <span>${escapeHtml(statusLabel(img.status))}</span>
           </div>`;

    return `
        <div class="gallery-item${ready ? '' : ' is-pending processing'}" data-id="${img.id}">
            <div class="image-wrapper" ${ready ? 'data-action="view"' : ''} role="button" tabindex="0">
                ${media}
            </div>
            <div class="image-info">
                <div class="image-info-text">
                    <div class="image-caption ${caption ? '' : 'image-no-caption'}">
                        ${escapeHtml(caption || 'No caption')}
                    </div>
                    <div class="image-meta">
                        ${img.shared ? '<span class="badge badge-success">Shared</span>' : ''}
                        ${img.collection ? `<span class="badge badge-secondary">${escapeHtml(img.collection)}</span>` : ''}
                    </div>
                </div>
                ${tileMenuHtml([
                    { action: 'edit', icon: 'fa-pen', label: 'Edit caption' },
                    ...(auth.isLimited() ? [] : [{ action: 'share', icon: 'fa-share-nodes', label: 'Share' }]),
                    ...(ready ? [{ action: 'download', icon: 'fa-download', label: 'Download original' }] : []),
                    { action: 'delete', icon: 'fa-trash', label: 'Delete', danger: true },
                ])}
            </div>
        </div>`;
}

function renderTiles() {
    const body = document.getElementById('gallery-body');
    if (!body) return;

    const images = visibleImages();

    if (!images.length) {
        body.innerHTML = state.images.length
            ? `<div class="empty-state"><i class="fa-solid fa-magnifying-glass"></i><p>No photos match that search.</p></div>`
            : `<div class="empty-state"><i class="fa-solid fa-images"></i>
                   <h2>No photos yet</h2>
                   <p>Upload your first photo to get started.</p>
               </div>`;
        return;
    }

    body.innerHTML = `<div class="gallery-grid">${images.map(tileHtml).join('')}</div>`;
    body.querySelectorAll('.gallery-item').forEach(bindTile);
    bindTileMenuDismissal();
}

function bindTile(tile) {
    const id = Number(tile.dataset.id);
    const image = () => state.images.find((i) => i.id === id);

    tile.addEventListener('click', async (e) => {
        const action = e.target.closest('[data-action]')?.dataset.action;
        if (!action) return;
        e.stopPropagation();

        const img = image();
        if (!img) return;

        if (action === 'menu') {
            toggleTileMenu(tile);
            return;
        }

        // Any real action closes the menu it was chosen from.
        closeTileMenus();

        if (action === 'view') {
            const ready = visibleImages().filter((i) => i.status === 'ready');
            openViewer(ready, ready.findIndex((i) => i.id === id));
        } else if (action === 'edit') {
            openEditModal(img);
        } else if (action === 'share') {
            openShareModal(img);
        } else if (action === 'download') {
            await downloadOriginal(img);
        } else if (action === 'delete') {
            await deleteImage(img);
        }
    });

    tile.querySelector('[data-action="view"]')?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.target.click();
        }
    });
}

// ---- Upload ----------------------------------------------------------------

async function uploadFiles(files) {
    const status = document.getElementById('upload-status');
    const accepted = [];

    for (const file of files) {
        if (!uploadType(file).startsWith('image/')) {
            showToast(`${file.name} isn't an image`, 'error');
        } else if (file.size > MAX_UPLOAD_BYTES) {
            showToast(`${file.name} is larger than 32MB`, 'error');
        } else {
            accepted.push(file);
        }
    }
    if (!accepted.length) return;

    status.innerHTML = accepted.map((f, i) => `
        <div class="form-group" data-upload="${i}">
            <div class="text-muted">${escapeHtml(f.name)} — ${formatBytes(f.size)}</div>
            <div class="upload-progress"><div class="upload-progress-bar"></div></div>
        </div>`).join('');

    let uploaded = 0;
    for (const [i, file] of accepted.entries()) {
        const bar = status.querySelector(`[data-upload="${i}"] .upload-progress-bar`);
        const collection = state.collection || null;
        let id = null;
        try {
            let upload;
            ({ id, upload } = await api.createUpload({
                contentType: uploadType(file),
                sizeBytes: file.size,
                caption: file.name.replace(/\.[^.]+$/, ''),
                collection,
            }));
            state.uploads.set(id, { name: file.name, collection });
            await api.uploadToS3(upload, file, (pct) => {
                bar.style.width = `${Math.round(pct * 100)}%`;
            });
            bar.style.width = '100%';
            uploaded += 1;
        } catch (err) {
            // The bytes never arrived, so there is no check to wait for. The row stays as a
            // placeholder the owner can delete, exactly as before.
            if (id !== null) state.uploads.delete(id);
            showToast(`${file.name}: ${err.message ?? 'upload failed'}`, 'error');
            bar.style.background = 'var(--color-danger)';
        }
    }

    status.innerHTML = '';
    if (uploaded) {
        showToast(`${uploaded} photo${uploaded === 1 ? '' : 's'} uploaded — checking ${uploaded === 1 ? 'it' : 'them'} now`, 'success');
        await loadImages();
    }
}

/**
 * Says what became of uploads started from this page, once each has settled.
 *
 * Only judged when the list being shown would include the upload: with a different collection
 * selected, an upload is absent because of the filter, not because it failed.
 */
function reportFinishedUploads() {
    for (const [id, upload] of state.uploads) {
        if (state.collection && state.collection !== upload.collection) continue;

        const image = state.images.find((i) => i.id === id);
        if (image && IN_PROGRESS.has(image.status)) continue;

        state.uploads.delete(id);
        if (!image) {
            showToast(`${upload.name}: upload failed — the file couldn't be added`, 'error');
        } else if (image.status === 'unchecked') {
            showToast(`${upload.name} couldn't be checked, so it won't be shown`, 'error');
        }
    }
}

/**
 * Uploads are checked and their derivatives generated asynchronously, so a freshly uploaded
 * image is pending or checking until that finishes. Poll while any are outstanding, with a
 * hard cap so a genuinely stuck image can't leave the page polling forever.
 */
function schedulePendingPoll(attempt = 0) {
    stopPolling();
    if (attempt >= PENDING_POLL_LIMIT) return;
    if (!state.images.some((i) => IN_PROGRESS.has(i.status)) && state.uploads.size === 0) return;

    state.pollTimer = setTimeout(async () => {
        try {
            const res = await api.listImages(state.collection ? { collection: state.collection } : {});
            state.images = res.images ?? [];
            reportFinishedUploads();
            renderTiles();
            schedulePendingPoll(attempt + 1);
        } catch {
            // A transient failure shouldn't kill the page; stop polling and let the
            // user refresh. The images are fine either way.
        }
    }, PENDING_POLL_MS);
}

function stopPolling() {
    if (state.pollTimer) clearTimeout(state.pollTimer);
    state.pollTimer = null;
}

// ---- Actions ---------------------------------------------------------------

async function downloadOriginal(img) {
    try {
        const { url } = await api.getDownloadUrl(img.id);
        // The presigned URL carries a Content-Disposition attachment header, so a plain
        // navigation downloads rather than displays it.
        window.location.href = url;
    } catch (err) {
        showToast(err.message ?? 'Could not prepare that download', 'error');
    }
}

async function deleteImage(img) {
    if (!confirm(`Delete "${img.caption || 'this photo'}"? This cannot be undone.`)) return;
    try {
        await api.deleteImage(img.id);
        state.images = state.images.filter((i) => i.id !== img.id);
        state.uploads.delete(img.id);
        renderTiles();
        showToast('Photo deleted', 'success');
    } catch (err) {
        showToast(err.message ?? 'Could not delete that photo', 'error');
    }
}

// ---- Modals ----------------------------------------------------------------

function openModal(title, bodyHtml, onMount) {
    const overlay = document.createElement('div');
    overlay.className = 'modal show';
    overlay.innerHTML = `
        <div class="modal-content">
            <div class="modal-header">
                <h2>${escapeHtml(title)}</h2>
                <button type="button" class="modal-close" aria-label="Close">&times;</button>
            </div>
            <div class="modal-body">${bodyHtml}</div>
        </div>`;
    document.body.appendChild(overlay);
    document.body.classList.add('modal-open');

    const close = () => {
        overlay.remove();
        document.body.classList.remove('modal-open');
        document.removeEventListener('keydown', onKey);
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };

    overlay.querySelector('.modal-close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', onKey);

    onMount?.(overlay, close);
    return close;
}

function openEditModal(img) {
    openModal('Edit photo', `
        <form id="edit-form" novalidate>
            <div class="form-group">
                <label for="edit-caption">Caption</label>
                <input type="text" id="edit-caption" value="${escapeHtml(img.caption ?? '')}" maxlength="255">
            </div>
            <div class="form-group">
                <label for="edit-collection">Collection</label>
                <input type="text" id="edit-collection" value="${escapeHtml(img.collection ?? '')}" maxlength="100"
                       list="collection-list" placeholder="e.g. Holidays">
                <datalist id="collection-list">
                    ${state.collections.map((c) => `<option value="${escapeHtml(c.name)}">`).join('')}
                </datalist>
            </div>
            ${auth.isLimited() ? '' : `
            <div class="form-group setting-row">
                <label>
                    <input type="checkbox" id="edit-shared" ${img.shared ? 'checked' : ''}>
                    Share with everyone in the family
                </label>
                <p class="form-hint">Leave this off to share with specific people instead.</p>
            </div>`}
            <div class="modal-actions">
                <button type="submit" class="btn btn-primary">Save</button>
            </div>
        </form>`, (overlay, close) => {
        overlay.querySelector('#edit-form').addEventListener('submit', async (e) => {
            e.preventDefault();
            try {
                const updated = await api.updateImage(img.id, {
                    caption: overlay.querySelector('#edit-caption').value.trim() || null,
                    collection: overlay.querySelector('#edit-collection').value.trim() || null,
                    shared: overlay.querySelector('#edit-shared')?.checked ?? false,
                });
                Object.assign(img, updated);
                close();
                showToast('Photo updated', 'success');
                await loadImages();
            } catch (err) {
                showToast(err.message ?? 'Could not save those changes', 'error');
            }
        });
    });
}

async function openShareModal(img) {
    let targets = [];
    let current = [];
    try {
        const [t, s] = await Promise.all([api.listShareTargets(), api.getShares(img.id)]);
        targets = t.users ?? [];
        current = s.user_ids ?? [];
    } catch (err) {
        showToast(err.message ?? 'Could not load sharing options', 'error');
        return;
    }

    // "Everyone" is the images.shared flag, the same one the edit dialog sets. It belongs here
    // too: this is the dialog people open when they want to share something, and having the
    // broadest option live somewhere else made it look as though the only choice was per person.
    const peopleList = targets.length
        ? `<div class="share-people-list">
            ${targets.map((u) => `
                <label class="share-person">
                    <input type="checkbox" class="share-person-check" value="${u.id}" ${current.includes(u.id) ? 'checked' : ''}>
                    <span>${escapeHtml(u.full_name)}</span>
                </label>`).join('')}
           </div>`
        : `<p class="text-muted">There's nobody else in the portal to share with individually yet.</p>`;

    openModal('Share photo', `
        <p class="text-muted">Choose who can see this photo.</p>
        <label class="share-person share-everyone">
            <input type="checkbox" id="share-everyone" ${img.shared ? 'checked' : ''}>
            <span>Everyone in the family</span>
        </label>
        ${peopleList}
        <p class="form-hint" id="share-hint"></p>
        <div class="modal-actions">
            <button type="button" id="save-shares-btn" class="btn btn-primary">Save sharing</button>
        </div>`, (overlay, close) => {
        const everyoneBox = overlay.querySelector('#share-everyone');
        const hint = overlay.querySelector('#share-hint');
        const people = [...overlay.querySelectorAll('.share-person-check')];

        // Ticking everyone leaves the per-person ticks as they are but stops them being edited:
        // they still mean something the moment "everyone" is turned off again, so they are
        // disabled rather than cleared. Disabled boxes still match :checked when saving.
        const sync = () => {
            const everyone = everyoneBox.checked;
            people.forEach((box) => { box.disabled = everyone; });
            overlay.querySelector('.share-people-list')?.classList.toggle('is-muted', everyone);
            hint.textContent = everyone
                ? 'The whole family can see this photo. The ticks above apply again if you turn this off.'
                : 'Only the people you tick will see it.';
        };
        everyoneBox.addEventListener('change', sync);
        sync();

        overlay.querySelector('#save-shares-btn').addEventListener('click', async (e) => {
            const btn = e.currentTarget;
            btn.disabled = true;
            const everyone = everyoneBox.checked;
            const ids = people.filter((box) => box.checked).map((box) => Number(box.value));
            try {
                if (everyone !== img.shared) {
                    // The caption and collection go back unchanged: this endpoint sets all three.
                    const updated = await api.updateImage(img.id, {
                        caption: img.caption ?? null,
                        collection: img.collection ?? null,
                        shared: everyone,
                    });
                    Object.assign(img, updated);
                }
                await api.setShares(img.id, ids);
                close();
                showToast('Sharing updated', 'success');
                await loadImages();
            } catch (err) {
                showToast(err.message ?? 'Could not update sharing', 'error');
                btn.disabled = false;
            }
        });
    });
}
